import {
    Box,
    ButtonGroup,
    Divider,
    List,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Td,
    Text,
    useDisclosure,
    useToast
} from "@chakra-ui/react"
import {
    Alert,
    Breadcrumb,
    Container,
    Form,
    Heading,
    IconButton,
    Profile,
    Row,
    Searchable,
    Show,
    Table
} from "components"
import {API, Mask, Moment} from "config";
import {useBlackBNF} from "hooks"
import {useState, useEffect} from "react"
import {useNavigate} from "react-router";

export default function BlackBNF() {

    const toast = useToast()
    const [current, setCurrent] = useState(100);
    const [term, setTerm] = useState()
    const [period, setPeriod] = useState([Moment().startOf('month').toDate(), Moment().endOf('month').toDate()])
    const [loading, setLoading] = useState(false)
    const list = useBlackBNF(current, term, period)

    const handleExport = async () => {
        setLoading(true)
        try {
            const callback = await API.get(`/tools/black-bnf?list=${current}${term ? `&term=${term}` : ''}&start_at=${Moment(period[0]).unix()}&end_at=${Moment(period[1]).unix()}&export=1`)
            window.open(callback?.data?.download_url)
        } catch (err) {
            toast({
                title: 'Não foi possível exportar a lista. Tente novamente.',
                description: err?.response?.data?.message,
                status: 'warning'
            })
        }
        setLoading(false)
    }

    const isBlack100 = current === 100
    const columns = isBlack100
        ? [
            {children: 'Cliente'},
            {children: 'Qtd downline', isNumeric: true},
            {children: 'Total em compras', isNumeric: true},
            {children: 'Downlines', isNumeric: true}
        ]
        : [
            {children: 'Cliente'},
            {children: 'Total em Compras', isNumeric: true}
        ];

    const blackOptions = [
        {label: 'BNF 100', value: 100},
        {label: 'BNF 200', value: 200},
        {label: 'BNF 400', value: 400}
    ]

    useEffect(() => {
        setPeriod([Moment().startOf('month').toDate(), Moment().endOf('month').toDate()])
    }, [current])

    return (
        <Container>

            <Breadcrumb prev={{path: '/tools', label: 'Ferramentas'}} current="Black BNF"/>

            <Heading title="Black BNF">
                <ButtonGroup size="xs" isDisabled={list.isFetching}>
                    <IconButton label="Atualizar" icon="refresh" isLoading={list.isFetching} onClick={list.refetch}/>
                </ButtonGroup>
            </Heading>

            <Divider my={4}/>

            <Searchable value={term} setValue={setTerm} placeholder="Buscar pelo id, código ou nome...">
                <Form.Select
                    value={current}
                    onChange={e => setCurrent(Number(e.target.value))}
                    options={blackOptions}
                />
                <Box w="200" key={current}>
                    <Form.Interval
                        initial={period}
                        onSelect={setPeriod}
                        onChange={(dates) => {
                            if (current === 100) {
                                const [start, end] = dates

                                if (start && !end) {
                                    setPeriod([start, null])
                                    return
                                }

                                if (start && end) {
                                    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()

                                    if (sameMonth) {
                                        setPeriod([start, end])
                                    } else {
                                        setPeriod([start, null])
                                    }
                                    return
                                }

                                setPeriod(dates)
                            } else {
                                setPeriod([Moment().startOf('month').toDate(), Moment().endOf('month').toDate()])
                            }
                        }}
                        filterDate={(date) => {
                            if (current !== 100) return true
                            const [start, end] = period
                            if (!start || end) return true
                            return date.getMonth() === start.getMonth() && date.getFullYear() === start.getFullYear()
                        }}
                    />
                </Box>
                <IconButton
                    isLoading={loading}
                    isDisabled={list.data?.length === 0 && !list.isFetching}
                    icon="csv" label="Exportar para .csv"
                    colorScheme="blue"
                    onClick={handleExport}
                />
            </Searchable>

            <Show visible={list.data?.length > 0}>
                <Alert my={4}>Foram encontrados <b>{list.data?.length}</b> clientes
                    que fazem parte da lista <b>BNF {current}</b> no período
                    de <b>{Moment(period[0]).format('LL')}</b> até <b>{Moment(period[1]).format('LL')}</b>.
                </Alert>
            </Show>

            <Table columns={columns} mt={4}>
                {list.data?.map(item => <Item key={item.upline_id || item.id} data={item} current={current}/>)}
            </Table>

            {list.data?.length === 0 && !list.isFetching && (
                <Box
                    textAlign="center"
                    py={10}
                    px={4}
                    mt={4}
                    bg="gray.700"
                    borderRadius="md"
                >
                    <Text color="gray.100" mb={2} fontWeight="medium">
                        Nenhum resultado encontrado
                    </Text>
                    <Text color="gray.200" fontSize="sm">
                        Não há nenhuma informação disponível para o período selecionado.
                    </Text>
                </Box>
            )}
        </Container>
    )
}

const Item = ({data, current}) => {
    const navigate = useNavigate()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const isBlack100 = current === 100

    const nomesList = data.downline_names?.split(', ').map(nome => nome.trim()) || []
    const valoresList = data.downline_values?.split(', ').map(valor => valor.trim()) || []

    const downlinesList = nomesList.map((nome, index) => ({
        nome,
        valor: valoresList[index] || '0'
    }))

    if (isBlack100) {
        return (
            <>
                <Row>
                    <Td onClick={() => navigate(`/consultants/${data.upline_id}`)} cursor="pointer">
                        <Profile name={data.upline_name}/>
                    </Td>
                    <Td isNumeric onClick={() => navigate(`/consultants/${data.upline_id}`)} cursor="pointer">
                        {data.qty_downlines_with_goal}
                    </Td>
                    <Td isNumeric onClick={() => navigate(`/consultants/${data.upline_id}`)} cursor="pointer">
                        R$ {Mask.price(data.upline_total)}
                    </Td>
                    <Td isNumeric>
                        <IconButton
                            size="sm"
                            icon="view"
                            label="Ver downlines"
                            colorScheme="blue"
                            variant="ghost"
                            onClick={onOpen}
                        />
                    </Td>
                </Row>

                <Modal isOpen={isOpen} onClose={onClose} size="xl">
                    <ModalOverlay/>
                    <ModalContent>
                        <ModalHeader>
                            Downlines - {data.upline_name}
                        </ModalHeader>
                        <ModalCloseButton/>
                        <ModalBody pb={6}>
                            <Table
                                columns={[
                                    { children: 'Nome' },
                                    { children: 'Total em Compras', isNumeric: true }
                                ]}
                                variant="simple"
                                size="sm"
                            >
                                {downlinesList.map((downline, index) => (
                                    <Row key={index}>
                                        <Td>{downline.nome}</Td>
                                        <Td isNumeric>R$ {Mask.price(downline.valor)}</Td>
                                    </Row>
                                ))}
                            </Table>
                            {downlinesList.length === 0 && (
                                <Text color="gray.500" textAlign="center" py={4}>
                                    Nenhum downline encontrado
                                </Text>
                            )}
                        </ModalBody>
                    </ModalContent>
                </Modal>
            </>
        )
    }

    return (
        <Row onClick={() => navigate(`/consultants/${data.id}`)}>
            <Td>
                <Profile name={data.customer}/>
            </Td>
            <Td isNumeric>
                R$ {Mask.price(data.total_amount)}
            </Td>
        </Row>
    )
}